import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import './FertRawMaterialPopup.css';
import './ContainerPopup.css';

const WEIGHT_UNITS = ['g', 'oz', 'lb', 'kg', 'ml', 'L'];
const emptyRow = () => ({ containerID: '', containerName: '', containerWeight: '', containerWeightMeasure: 'g' });

const ContainerPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [rows,      setRows]      = useState([emptyRow()]);
    const [saving,    setSaving]    = useState(false);
    const [error,     setError]     = useState('');
    const [rowErrors, setRowErrors] = useState({});

    const firstInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setRows([{
                containerID:            editData.containerID            || '',
                containerName:          editData.containerName          || '',
                containerWeight:        editData.containerWeight        || '',
                containerWeightMeasure: editData.containerWeightMeasure || 'g',
            }]);
        } else {
            setRows([emptyRow()]);
        }
        setError(''); setRowErrors({});
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const updateRow = (i, field, val) =>
        setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
    const addRow    = () => setRows(prev => [...prev, emptyRow()]);
    const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

    const handleSave = async () => {
        setError(''); setRowErrors({});

        if (editData) {
            const r = rows[0];
            if (!r.containerName.trim()) { setError('Container Name is required'); return; }
            setSaving(true);
            try {
                await api.put(`/api/containers-batch/${editData.containerID}`, {
                    containerName:          r.containerName.trim(),
                    containerWeight:        r.containerWeight || null,
                    containerWeightMeasure: r.containerWeightMeasure || null,
                });
                onSaved(); onClose();
            } catch (err) {
                setError(err.response?.data?.error || 'Save failed');
            } finally { setSaving(false); }
            return;
        }

        // Add mode — validate
        const newRowErrors = {};
        rows.forEach((r, i) => {
            const hasAny = r.containerID.trim() || r.containerName.trim();
            if (hasAny && !r.containerID.trim())   newRowErrors[i] = 'Container ID required';
            else if (hasAny && !r.containerName.trim()) newRowErrors[i] = 'Container Name required';
        });
        if (Object.keys(newRowErrors).length) { setRowErrors(newRowErrors); return; }

        const valid = rows.filter(r => r.containerID.trim() && r.containerName.trim());
        if (valid.length === 0) { setError('Please fill in at least one container'); return; }

        setSaving(true);
        try {
            const res = await api.post(`/api/containers-batch`, { containers: valid });
            if (res.data.errors?.length) {
                setError(res.data.errors.join(' · '));
                if (res.data.inserted.length > 0) onSaved();
            } else {
                onSaved(); onClose();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const filledCount = rows.filter(r => r.containerID.trim() && r.containerName.trim()).length;

    return (
        <div className="frm-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="frm-popup cnt-popup">

                {/* Header */}
                <div className="frm-header">
                    <div>
                        <div className="frm-title">
                            {editData ? `Edit Container — ${editData.containerID}` : 'Add Containers'}
                        </div>
                        <div className="frm-subtitle">
                            {editData
                                ? 'Container ID cannot be changed'
                                : 'Fill in details for each container you want to add'}
                        </div>
                    </div>
                    <button className="frm-close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>

                {/* Body */}
                <div className="frm-body">

                    <div className="frm-rows">
                        {rows.map((row, i) => (
                            <div key={i}>
                                {/* Both add and edit use the same card grid — cleaner, no overflow */}
                                <div className={`cnt-card${rowErrors[i] ? ' cnt-card--error' : ''}`}>
                                    {/* Card header: index + remove */}
                                    {(!editData) && (
                                        <div className="cnt-card__header">
                                            <span className="cnt-card__num">#{i + 1}</span>
                                            {rows.length > 1 && (
                                                <button className="frm-remove" onClick={() => removeRow(i)} title="Remove">
                                                    <i className="fas fa-times" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="cnt-edit-grid">
                                        <div className="cnt-field">
                                            <span className="cnt-label">Container ID *</span>
                                            <input
                                                ref={i === 0 ? firstInputRef : null}
                                                className={`frm-input${editData ? ' cnt-id-readonly' : ''}`}
                                                value={row.containerID}
                                                readOnly={!!editData}
                                                onChange={e => updateRow(i, 'containerID', e.target.value)}
                                                placeholder="e.g. 1"
                                            />
                                        </div>
                                        <div className="cnt-field">
                                            <span className="cnt-label">Container Name *</span>
                                            <input
                                                ref={i === 0 && editData ? firstInputRef : null}
                                                className="frm-input"
                                                value={row.containerName}
                                                onChange={e => updateRow(i, 'containerName', e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && editData) handleSave(); }}
                                                placeholder="e.g. Small Packet"
                                            />
                                        </div>
                                        <div className="cnt-field cnt-field--weight">
                                            <span className="cnt-label">Weight</span>
                                            <div className="cnt-weight-row">
                                                <input className="frm-input cnt-weight-input"
                                                    type="number" min="0" step="0.01"
                                                    value={row.containerWeight}
                                                    onChange={e => updateRow(i, 'containerWeight', e.target.value)}
                                                    placeholder="e.g. 3.5" />
                                                <select className="frm-input cnt-unit-select"
                                                    value={row.containerWeightMeasure}
                                                    onChange={e => updateRow(i, 'containerWeightMeasure', e.target.value)}>
                                                    {WEIGHT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {rowErrors[i] && (
                                    <div className="brand-row-error" style={{ marginLeft: editData ? 0 : 32 }}>
                                        {rowErrors[i]}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {!editData && (
                        <button className="frm-add-row" onClick={addRow}>
                            <i className="fas fa-plus" />
                            Add another
                        </button>
                    )}

                    {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                </div>

                {/* Footer */}
                <div className="frm-footer">
                    <button className="frm-btn frm-btn--ghost" onClick={onClose}>Cancel</button>
                    <button className="frm-btn frm-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving
                            ? <><i className="fas fa-spinner fa-spin" /> Saving…</>
                            : editData
                                ? <><i className="fas fa-check" /> Save</>
                                : <><i className="fas fa-box" /> Add {filledCount > 1 ? `${filledCount} containers` : 'container'}</>
                        }
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ContainerPopup;
